import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { exportTimesheetsToQuickBooks, getTimesheetFilterOptions, getTimesheets, searchOpenProjects, TimesheetRow, uploadTimesheetsPrintReport } from './timesheetsApi'
import { TimesheetsFilters, TimesheetsGridRequest } from './timesheetsModel'
import { DatatableResults } from '../../common/datatables'

export const useTimesheetsQuery = (request: TimesheetsGridRequest) => {
  return useQuery<DatatableResults<TimesheetRow>>(['timesheets', request], () => getTimesheets(request), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({
        message: 'There was a problem loading the timesheets. Please try again after a few moments.'
      })
    }
  })
}

export const useTimesheetsInvalidator = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries(['timesheets'])
}

export const useTimesheetFilterOptionsQuery = () => {
  return useQuery(['timesheetFilterOptions'], getTimesheetFilterOptions, {
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({
        message: 'There was a problem loading the crew members. Please try again after a few moments.'
      })
    }
  })
}

export const useExportTimesheetsToQuickBooksMutation = (onSuccess?: (errors: string[]) => void) => {
  const invalidateTimesheets = useTimesheetsInvalidator()
  return useMutation((ids: string[]) => exportTimesheetsToQuickBooks(ids), {
    onSuccess: errors => {
      invalidateTimesheets()
      onSuccess?.(errors)
    },
    onError: () => {
      notification.error({
        message: 'There was an error exporting your selected time to QuickBooks. Please try again after a few moments.'
      })
    }
  })
}

export const useTimesheetsPrintReportMutation = (onSuccess: (url: string) => void) => {
  return useMutation((filters: TimesheetsFilters) => uploadTimesheetsPrintReport(filters), {
    onSuccess: result => {
      onSuccess(result.url)
    },
    onError: () => {
      notification.error({
        message: 'There was an error printing. Please try again after a few moments.'
      })
    }
  })
}

export const useOpenProjectsQuery = (search: string) => {
  return useQuery(['timesheetOpenProjects', search], () => searchOpenProjects(search), {
    enabled: search.trim().length > 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({
        message: 'There was a problem searching projects. Please try again after a few moments.'
      })
    }
  })
}
